/**
 * RTL layout utilities for Lightning.js absolute-coordinate canvas.
 *
 * Lightning.js does NOT reflow for RTL automatically — every x coordinate
 * must be mirrored manually. Use these helpers everywhere an x value or
 * directional concept appears in a component.
 */

export const STAGE_W = 1920;

/**
 * Mirror an LTR x coordinate to its RTL equivalent.
 * @param x - element's left edge in LTR
 * @param w - element's width
 */
export const mirrorX = (x: number, w: number): number => STAGE_W - x - w;

/**
 * Return the correct x position depending on layout direction.
 * @param ltrX - left-edge x in LTR
 * @param w    - element width
 * @param rtl  - true when Arabic / RTL is active
 */
export const rtlX = (ltrX: number, w: number, rtl: boolean): number =>
  rtl ? mirrorX(ltrX, w) : ltrX;

/**
 * Text alignment that follows the reading direction.
 */
export const rtlAlign = (rtl: boolean): "left" | "right" =>
  rtl ? "right" : "left";

/**
 * Flex row direction that follows the reading direction.
 */
export const rtlRow = (rtl: boolean): "row" | "row-reverse" =>
  rtl ? "row-reverse" : "row";

/**
 * Return the correct "start" navigation handler for a horizontal list.
 * In LTR "start" is Left; in RTL "start" is Right.
 * Usage:  onLeft={rtlNavStart(rtl, goToPrev)} onRight={rtlNavStart(rtl, () => null)}
 */
export const rtlNavStart = (
  rtl: boolean,
  handler: () => void,
): (() => void) | undefined => (rtl ? undefined : handler);

export const rtlNavEnd = (
  rtl: boolean,
  handler: () => void,
): (() => void) | undefined => (rtl ? handler : undefined);

/**
 * Progress-bar fill: in RTL the fill starts from the right edge.
 * Returns the x offset for the filled portion inside a container of `containerW`.
 */
export const rtlProgressX = (
  progress: number,
  containerW: number,
  rtl: boolean,
): number => (rtl ? containerW * (1 - progress) : 0);
